import { useState, type FC } from 'react'
import { Box, Button, InputBase, Stack } from '@mui/material'
import useTranslation from '@/hooks/useTranslation'
import type { GridFilterProps } from '@/components/grid/types'

export interface TextContainsModel {
  contains: string
}

export const isTextContainsModel = (model: unknown): model is TextContainsModel =>
  Boolean(
    model &&
      typeof model === 'object' &&
      typeof (model as TextContainsModel).contains === 'string' &&
      (model as TextContainsModel).contains.trim() !== '',
  )

/**
 * Single "contains" search over one column — the replacement for the station
 * grid's `agTextColumnFilter`. The grid only carries this model; the page
 * applies it, the way it does for any filter that is not a plain value set.
 */
const TextContainsFilter: FC<GridFilterProps<any>> = ({
  model,
  onChange,
  onClose,
}) => {
  const { t } = useTranslation()
  const [draft, setDraft] = useState(
    isTextContainsModel(model) ? model.contains : '',
  )

  const apply = () => {
    const value = draft.trim()
    onChange(value ? { contains: value } : null)
    onClose()
  }

  return (
    <Box sx={{ padding: '10px', minWidth: 220 }}>
      <InputBase
        autoFocus
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') apply()
        }}
        placeholder={t('commonActionsSearch')}
        sx={{
          width: '100%',
          fontSize: 13,
          color: 'text.main',
          border: '1px solid',
          borderColor: 'border.main',
          borderRadius: '6px',
          px: '8px',
          height: 32,
        }}
      />

      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.25 }}>
        <Button
          variant="secondary"
          size="small"
          onClick={() => {
            setDraft('')
            onChange(null)
            onClose()
          }}
        >
          {t('commonActionsReset')}
        </Button>
        <Button variant="primary" size="small" onClick={apply}>
          {t('commonActionsApply')}
        </Button>
      </Stack>
    </Box>
  )
}

export default TextContainsFilter
